import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";
import { FaEdit, FaMapMarkedAlt } from "react-icons/fa";

import myConfig from "../../configs/config";
import { dayReportMatchesFilters } from "./serviceOrderListFilters";
import { ServiceOrderProductsWithCopy } from "./ServiceOrderProductsPanel";
import {
  formatHectares,
  serviceOrderWorkProgress,
} from "./serviceOrderWorkProgress";
import "./ServiceOrderLandplotMapModal.css";
import "./PendingSchedules.css";
import "./ServiceOrderProductsPanel.css";

function formatWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function observationForDayReport(report, observations) {
  const uuid = String((report && report.uuid) || "").trim();
  const observationId = Number(report && report.observationId);
  const list = Array.isArray(observations) ? observations : [];
  if (Number.isFinite(observationId) && observationId > 0) {
    const byId = list.find((obs) => Number(obs.id) === observationId);
    if (byId) return byId;
  }
  if (!uuid) return null;
  return (
    list.find((obs) => {
      const plan = (obs && obs.service_order_plan) || {};
      return String(plan.day_report_uuid || "").trim() === uuid;
    }) || null
  );
}

function reportAsObservation(report) {
  const machineIdent = String((report && report.machineIdent) || "").trim();
  return {
    id: report.observationId || null,
    operationId: report.operationId || null,
    operationName: report.title,
    description: report.description,
    products_used: report.products,
    land_plot_names: report.landPlotNames,
    service_farm_names: report.farmName,
    start_date: report.startedAt,
    end_date: report.endedAt,
    executed_start_date: report.startedAt,
    executed_end_date: report.endedAt,
    confirmed: true,
    assigned_operators: report.operatorName
      ? [{ name: report.operatorName }]
      : [],
    service_order_plan: {
      origin: "relato_dia",
      day_report_uuid: report.uuid,
      machine_idents: machineIdent ? [machineIdent] : [],
    },
    field_executions: [],
  };
}

function DayReportActions({ obs, farmId, onEdit, t }) {
  const history = useHistory();
  const progress = serviceOrderWorkProgress(obs);
  const canEdit = Boolean(obs && obs.id && typeof onEdit === "function");

  const openTrailMap = () => {
    if (!progress.hasTrailTarget || !progress.fromDay || !progress.toDay) return;
    const params = new URLSearchParams({
      tab: "tracking",
      from: progress.fromDay,
      to: progress.toDay,
    });
    if (progress.machineIdents.length) {
      params.set("machines", progress.machineIdents.join(","));
    }
    if (progress.operatorIds.length) {
      params.set("operators", progress.operatorIds.join(","));
    }
    history.push(`/${farmId}/field-app/?${params.toString()}`);
  };

  return (
    <div className="pending-schedules-card__actions">
      {canEdit ? (
        <button
          type="button"
          className="btn btn-sm btn-default"
          onClick={() => onEdit(obs)}
        >
          <FaEdit style={{ marginRight: 6 }} />
          {t("Editar")}
        </button>
      ) : null}
      {obs.operationId ? (
        <Link
          to={`/${farmId}/operation/${obs.operationId}?filter_obs=${obs.id || ""}`}
          className="btn btn-sm btn-outline-success pending-schedules-tracking-btn"
          title={t("Ver rastreamento do período executado")}
        >
          {t("Ver na operação")}
        </Link>
      ) : null}
      <div className="pending-schedules-card__progress">
        {progress.plannedHa != null ? (
          <div className="pending-schedules-card__progress-figures">
            <div>
              <strong>{t("Hectares feitos")}:</strong>{" "}
              {formatHectares(progress.workedHa)} ha
            </div>
          </div>
        ) : null}
        <button
          type="button"
          className="pending-schedules-card__map-btn"
          onClick={openTrailMap}
          disabled={!progress.hasTrailTarget}
          title={t("Ver mapeamento da operação")}
          aria-label={t("Ver mapeamento da operação")}
        >
          <FaMapMarkedAlt aria-hidden />
        </button>
      </div>
    </div>
  );
}

function ServiceOrderDayReports({
  farmId,
  filters,
  t,
  observations = [],
  onEdit,
}) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!farmId) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    axios
      .get(`${myConfig.API_URL}/mobile/field-data/day-reports/`, {
        params: { farm_id: farmId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
        },
      })
      .then((response) => {
        if (cancelled) return;
        const rows = (response.data && response.data.results) || [];
        setReports(rows);
      })
      .catch(() => {
        if (!cancelled) {
          setReports([]);
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [farmId]);

  const visible = useMemo(
    () => reports.filter((report) => dayReportMatchesFilters(report, filters)),
    [reports, filters]
  );

  return (
    <section className="service-order-day-reports">
      <h4>{t("Relatos do dia")}</h4>
      <p className="service-order-day-reports__hint">
        {t(
          "O que o operador registrou no aplicativo, no talhão por onde a máquina passou."
        )}
      </p>
      {loading ? <p>{t("Carregando relatos do dia...")}</p> : null}
      {failed ? (
        <p>{t("Não foi possível carregar os relatos do dia.")}</p>
      ) : null}
      {!loading && !failed && visible.length === 0 ? (
        <p>{t("Nenhum relato do dia neste filtro.")}</p>
      ) : null}
      {visible.map((report) => {
        const linked = observationForDayReport(report, observations);
        const obs = linked || reportAsObservation(report);
        return (
          <article
            key={report.uuid || report.id}
            className="service-order-day-reports__card pending-schedules-card"
          >
            <header>
              <strong>{report.title || t("Relato do dia")}</strong>
              <span>{formatWhen(report.timestamp || report.startedAt)}</span>
            </header>
            {report.description ? (
              <p className="pending-schedules-card__description">
                {report.description}
              </p>
            ) : null}
            <div className="service-order-day-reports__meta">
              {report.operatorName ? (
                <span>
                  {t("Operador")}: {report.operatorName}
                </span>
              ) : null}
              {report.farmName ? (
                <span>
                  {t("Fazenda")}: {report.farmName}
                </span>
              ) : null}
              {report.machineIdent || report.machineName ? (
                <span>
                  {t("Máquina")}: {report.machineName || report.machineIdent}
                </span>
              ) : null}
              {report.landPlotNames ? (
                <span>
                  {t("Talhão")}: {report.landPlotNames}
                </span>
              ) : (
                <span>{t("Talhão não identificado pelo GPS.")}</span>
              )}
              {report.startedAt ? (
                <span>
                  {t("Começou")}: {formatWhen(report.startedAt)}
                </span>
              ) : null}
              {report.endedAt ? (
                <span>
                  {t("Parou")}: {formatWhen(report.endedAt)}
                </span>
              ) : null}
              {report.appliedAmount ? (
                <span>
                  {t("Quantidade aplicada")}: {report.appliedAmount}{" "}
                  {report.appliedUnit || ""}
                </span>
              ) : null}
            </div>
            <ServiceOrderProductsWithCopy obs={obs} t={t} />
            <DayReportActions
              obs={obs}
              farmId={farmId}
              onEdit={onEdit}
              t={t}
            />
          </article>
        );
      })}
    </section>
  );
}

export default ServiceOrderDayReports;
